import { useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServiceAdmin, serviceAdminAction } from "./use-service-admin.ts";
import { LogsTable } from "./LogsTable.tsx";
import { DetailDrawer } from "../../agentdna/DetailDrawer.tsx";
import { StatusBadge, type StatusLevel } from "../../../components/index.ts";
import { formatDateTime, isoAttr } from "../../../lib/datetime.ts";
import { membersErrorMessage, useTeamMembers } from "../../../lib/use-team-members.ts";
import type { TeamMember } from "../../../lib/management-client.ts";

/**
 * The IDP admin console, migrated from the IDP service's `/admin` UI into the control plane:
 *   - Dashboard: the OpenID provider's server configuration and the team's real members (the same
 *     roster as the Members page).
 *   - Registrations: the agent registration records from `/admin/api/registrations`.
 *   - Logs: the shared transaction-log viewer (row → right-side detail drawer).
 */

export function IdpConsole(props: { view: "dashboard" | "registrations" | "logs" }): ReactNode {
  if (props.view === "logs") return <LogsTable slug="idp" />;
  if (props.view === "registrations") return <Registrations />;
  return <Dashboard />;
}

/**
 * Map the IDP's exposure to a user-facing Port value: `"443"` when the service is externally
 * exposed (reached over `SVC_IDP` on the public host) and the literal `"internal"` otherwise. The
 * internal container port number is never an input to this function, so it can never leak into the
 * field (Fixes 2.7, 2.8). Exported so it can be unit/property-tested in isolation (task 10).
 */
export function portField(exposed: boolean): string {
  return exposed ? "443" : "internal";
}

/**
 * Parse the add-agent "AI Models" textarea into a clean array: split on newlines OR commas, trim
 * each entry, and drop empties. This is the exact split the submit path applies to the raw textarea
 * value (Fix 2.14); it mirrors the parity rule the KYA IDP worker uses for a string `aiModels`.
 * Exported so it can be unit-tested in isolation (task 10), the same way `portField` is.
 */
export function parseAiModels(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * The IDP dashboard. The server configuration is the provider's fixed OpenID configuration (the same
 * constants the IDP renders on its own `/admin` dashboard) — the provider's own static facts, not
 * fabricated telemetry. Alongside it, the Team members card lists the signed-in operator's real
 * team (see {@link TeamMembersCard}); there are no hard-coded users.
 */
function Dashboard(): ReactNode {
  const externallyExposed = true; // the control plane reaches IDP over SVC_IDP on the public host (443)
  const config: ReadonlyArray<readonly [string, string]> = [
    ["Server Name", "OATHERA OpenID Provider"],
    ["Port", portField(externallyExposed)],
    ["Issuer URL", "https://idp.oathera.org"],
    ["Response Types", "code"],
    ["Grant Types", "authorization_code"],
    ["Scopes", "openid, email, profile"],
    ["Subject Types", "public"],
    ["Signing Algorithms", "EdDSA"],
    ["Code Challenge Methods", "S256"],
  ];

  // The display name heads the configuration card; the rest of the fixed OIDC
  // configuration fills the aligned key/value grid below it. Three scannable
  // tiles summarise the facts an operator checks first (port, protocol, flow).
  const [serverNameLabel, serverName] = config[0]!;
  // Drop Port from the grid — it is surfaced as the "Port" stat tile above, so
  // showing it twice would both read redundantly and duplicate the "443" node.
  // All preservation-required rows (Issuer URL … Code Challenge Methods) remain.
  const configRows = config.filter(([k]) => k !== "Server Name" && k !== "Port");

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-adm2__stats" aria-label="Provider summary">
        <StatTile label="Port" value={portField(externallyExposed)} />
        <StatTile label="Protocol" value="OIDC" />
        <StatTile label="Flow" value="PKCE" />
      </div>

      <div className="svc-adm2__grid">
        <section className="svc-adm2__card" aria-label="Server configuration">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Server configuration</h3>
          </div>
          <div className="svc-admin__section">
            <h4 className="svc-admin__section-title">{serverNameLabel}</h4>
            <p className="svc-admin__stat-value" style={{ fontSize: "16px" }}>{serverName}</p>
          </div>
          <dl className="svc-adm2__kv">
            {configRows.map(([k, v]) => (
              <div key={k} className="svc-admin__kv-row">
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <TeamMembersCard />
      </div>
    </div>
  );
}

/** The status → badge mapping for a team member (same three statuses the Members panel receives). */
function memberStatusBadge(status: TeamMember["status"]): { level: StatusLevel; label: string } {
  if (status === "active") return { level: "healthy", label: "Active" };
  if (status === "invited") return { level: "warn", label: "Invited" };
  return { level: "unknown", label: "Suspended" };
}

/**
 * The signed-in operator's REAL team members, from the same hook (query key, fetcher, error
 * vocabulary) as the Members page "Team members" panel: `GET /api/v1/team/members`, tenant resolved
 * server-side from the session — no tenant parameter is sent. The IDP admin API exposes no user
 * directory, so the team roster is the sole source. Rows are shown in the order the API returns them.
 */
function TeamMembersCard(): ReactNode {
  const query = useTeamMembers();
  const members = query.data ?? [];
  return (
    <section className="svc-adm2__card" aria-label="Team members">
      <div className="svc-adm2__card-head">
        <h3 className="svc-admin__h">Team members</h3>
        {query.isSuccess && (
          <span className="svc-adm2__card-sub">{members.length} {members.length === 1 ? "member" : "members"}</span>
        )}
      </div>
      {query.isError ? (
        <div className="state-box" data-kind="error" role="alert">
          <strong>Could not load team members</strong>
          <span>{membersErrorMessage(query.error)}</span>
        </div>
      ) : query.isPending ? (
        <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching team members.</span></div>
      ) : members.length === 0 ? (
        <div className="state-box" data-kind="empty"><strong>No members yet.</strong></div>
      ) : (
        <div className="svc-adm2__scroll">
          <table className="svc-adm2__table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const name = m.displayName !== undefined && m.displayName.trim() !== "" ? m.displayName : m.email;
                const badge = memberStatusBadge(m.status);
                return (
                  <tr key={m.id}>
                    <td>
                      <span className="svc-adm2__user">
                        <span className="svc-adm2__avatar" aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
                        {name}
                      </span>
                    </td>
                    <td className="svc-adm2__mono">{m.email}</td>
                    <td>{m.role}</td>
                    <td><StatusBadge level={badge.level} label={badge.label} size="sm" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * `GET /admin/api/registrations/summary`: the honest size of the IDP's registration store. The store
 * is in memory, so the count covers one container instance (`scope: "process"`) since it started
 * (`since`) and resets when the container restarts.
 */
interface RegistrationSummary {
  readonly count: number;
  readonly by_status?: { readonly pending: number; readonly enrolled: number; readonly revoked: number };
  readonly scope?: string;
  readonly since?: string;
}

interface Registration {
  readonly token: string;
  readonly status?: string;
  readonly principal?: string;
  readonly label?: string;
  readonly createdAt?: string;
  readonly [k: string]: unknown;
}

/**
 * An AIS-enrolled agent, as read from the AIS admin API over `SVC_AIS` (slug "ais", subpath
 * "agents"). Mirrors the AIS console's `Agent` shape; `principal` carries the operator's identity
 * (we render `principal.email`). The IDP console surfaces these read-only below its own
 * registration records so an operator sees both the IDP registrations and the agents AIS has
 * enrolled for the same account (Fix 2.12).
 */
interface AisAgent {
  readonly agentId: string;
  readonly principal?: { readonly email?: string; readonly name?: string };
  readonly label?: string;
  readonly platform?: string;
  readonly enrolledAt?: string;
  readonly revoked?: boolean;
  readonly status?: string;
  readonly ais?: unknown;
  readonly [k: string]: unknown;
}

/** The add-agent create body POSTed to `/api/v1/service-admin/idp/registrations` (Fix 2.14). */
interface CreateAgentBody {
  readonly name: string;
  readonly os: string;
  readonly version: string;
  readonly aiProvider: string;
  readonly aiModels: readonly string[];
}

function Registrations(): ReactNode {
  // All hooks are called unconditionally, before any early return, to respect the Rules of Hooks
  // (this component has early returns for the error/pending states below).
  const [mode, setMode] = useState<"list" | "add">("list");
  const query = useServiceAdmin<readonly Registration[]>("idp", "registrations", ["registrations"]);
  const agentsQuery = useServiceAdmin<readonly AisAgent[]>("ais", "agents", ["agents"]);
  const summaryQuery = useServiceAdmin<RegistrationSummary>("idp", "registrations/summary", ["registrations", "summary"]);
  const queryClient = useQueryClient();

  const nameId = useId();
  const osId = useId();
  const versionId = useId();
  const providerId = useId();
  const modelsId = useId();
  const drawerTitleId = useId();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const rows = query.data ?? [];

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const os = String(data.get("os") ?? "Linux");
    const version = String(data.get("version") ?? "").trim();
    const aiProvider = String(data.get("aiProvider") ?? "").trim();
    const aiModels = parseAiModels(String(data.get("aiModels") ?? ""));
    const body: CreateAgentBody = { name, os, version, aiProvider, aiModels };

    setSubmitting(true);
    setSubmitError(null);
    try {
      await serviceAdminAction("idp", "registrations", body);
      await queryClient.invalidateQueries({ queryKey: ["service-admin", "idp", "registrations"] });
      setMode("list");
    } catch (err) {
      // Surface any validation / HTTP error (including 404/405 before the KYA route is deployed)
      // and stay in add mode so the operator can correct and retry.
      setSubmitError(err instanceof Error ? err.message : "The agent could not be registered.");
    } finally {
      setSubmitting(false);
    }
  };

  // Load/error states for the registrations read keep their existing shape.
  if (query.isError) {
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load registrations</strong>
        <span>The IDP admin service could not be reached. {query.error.message}</span>
      </div>
    );
  }
  if (query.isPending) {
    return <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching registrations.</span></div>;
  }

  const enrolledCount = agentsQuery.data?.length ?? 0;
  // The tile counts what THIS IDP container holds in memory. Prefer the summary endpoint; if it is
  // unavailable (e.g. an older IDP image) fall back to the list length, which is the same store.
  const summary = summaryQuery.data;
  const summaryCount = typeof summary?.count === "number" && Number.isFinite(summary.count) ? summary.count : null;
  const since = typeof summary?.since === "string" && Number.isFinite(Date.parse(summary.since)) ? summary.since : null;
  const registeredValue = summaryCount !== null ? summaryCount : summaryQuery.isPending ? null : rows.length;

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-adm2__stats" aria-label="Registration summary">
        <StatTile
          label="Registered (this instance)"
          value={registeredValue === null ? "…" : String(registeredValue)}
          caption={since !== null ? (
            <>in memory, since <time dateTime={isoAttr(Date.parse(since))}>{safeDate(since)}</time></>
          ) : undefined}
          note="The count resets when the IDP container restarts."
        />
        <StatTile label="Enrolled (AIS)" value={agentsQuery.isPending ? "…" : String(enrolledCount)} />
      </div>

      <section className="svc-adm2__card" aria-label="Agent registrations">
        <div className="svc-adm2__card-head">
          <h3 className="svc-admin__h">Agent Registrations</h3>
          <button type="button" className="svc-admin__btn" onClick={() => { setSubmitError(null); setMode("add"); }}>
            + Add Agent
          </button>
        </div>
        {rows.length === 0 ? (
          <div className="state-box" data-kind="empty"><strong>No agents registered.</strong></div>
        ) : (
          <ul className="svc-adm2__rows">
            {rows.map((r) => {
              const status = (r.status ?? "unknown").toLowerCase();
              return (
                <li key={r.token}>
                  <div className="svc-admin__row" data-static="true">
                    <StatusBadge
                      level={status === "enrolled" ? "healthy" : status === "revoked" ? "error" : status === "pending" ? "warn" : "unknown"}
                      label={status.charAt(0).toUpperCase() + status.slice(1)}
                      size="sm"
                    />
                    <span className="svc-admin__row-subject">{r.principal ?? r.label ?? r.token.slice(0, 12)}</span>
                    {r.createdAt !== undefined && (
                      <time className="svc-admin__row-time" dateTime={isoAttr(Date.parse(r.createdAt))}>{safeDate(r.createdAt)}</time>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="svc-adm2__card" aria-label="Enrolled agents from AIS">
        <div className="svc-adm2__card-head">
          <h3 className="svc-admin__h">Enrolled Agents (from AIS)</h3>
        </div>
        <EnrolledAgents query={agentsQuery} />
      </section>

      {/* The "+ Add Agent" form opens in the right-side DetailDrawer (flyout) over the list. */}
      <DetailDrawer open={mode === "add"} onClose={() => setMode("list")} labelledBy={drawerTitleId}>
        <header className="drawer-head">
          <div className="drawer-head__title">
            <h2 id={drawerTitleId} className="drawer-head__heading">Add Agent</h2>
            <span className="drawer-head__id">Register a new agent for this IDP</span>
          </div>
          <button type="button" className="drawer-head__close" onClick={() => setMode("list")} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div className="drawer-body">
          <form className="svc-admin__form" onSubmit={(e) => void onSubmit(e)}>
            <div className="svc-admin__field">
              <label htmlFor={nameId}>Agent Name</label>
              <input id={nameId} name="name" type="text" maxLength={64} required />
            </div>
            <div className="svc-admin__field">
              <label htmlFor={osId}>Operating System</label>
              <select id={osId} name="os" defaultValue="Linux">
                <option value="Linux">Linux</option>
                <option value="macOS">macOS</option>
                <option value="Windows">Windows</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="svc-admin__field">
              <label htmlFor={versionId}>Version</label>
              <input id={versionId} name="version" type="text" maxLength={32} />
            </div>
            <div className="svc-admin__field">
              <label htmlFor={providerId}>AI Provider</label>
              <input id={providerId} name="aiProvider" type="text" maxLength={64} />
            </div>
            <div className="svc-admin__field">
              <label htmlFor={modelsId}>AI Models</label>
              <textarea id={modelsId} name="aiModels" rows={4} placeholder="One model per line" />
            </div>
            {submitError !== null && (
              <p className="svc-admin__err" role="alert">
                {submitError}
              </p>
            )}
            <div className="svc-admin__actions">
              <button type="button" className="svc-admin__btn" onClick={() => setMode("list")}>
                Cancel
              </button>
              <button type="submit" className="svc-admin__btn" disabled={submitting}>
                {submitting ? "Registering…" : "Register Agent"}
              </button>
            </div>
          </form>
        </div>
      </DetailDrawer>
    </div>
  );
}

/**
 * The read-only "Enrolled Agents (from AIS)" table. Shows the AIS-enrolled agents for the account
 * (Label / Agent ID / Principal / Platform / Enrolled / Status), matching the real IDP admin view.
 * Loading and error states reuse the shared state-box; the empty state is "No agents enrolled yet."
 */
type EnrolledAgentSortKey = "label" | "agentId" | "principal" | "platform" | "enrolledAt" | "status";
type EnrolledAgentSortDirection = "ascending" | "descending";

function enrolledAgentStatus(agent: AisAgent): string {
  if (agent.revoked === true) return "Revoked";
  if (agent.status === "suspended") return "Suspended";
  return agent.status ? agent.status.charAt(0).toUpperCase() + agent.status.slice(1) : "Active";
}

function compareEnrolledValues(left: string | number, right: string | number): number {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
}

/** The read-only, tenant-scoped table of agents enrolled by AIS. */
function EnrolledAgents(props: {
  query: ReturnType<typeof useServiceAdmin<readonly AisAgent[]>>;
}): ReactNode {
  const { query } = props;
  const [sortKey, setSortKey] = useState<EnrolledAgentSortKey>("enrolledAt");
  const [sortDirection, setSortDirection] = useState<EnrolledAgentSortDirection>("descending");
  const agents = query.data ?? [];
  const sortedAgents = useMemo(() => {
    const value = (agent: AisAgent): string | number => {
      switch (sortKey) {
        case "agentId": return agent.agentId;
        case "principal": return agent.principal?.email ?? agent.principal?.name ?? "";
        case "platform": return agent.platform ?? "";
        case "enrolledAt": {
          const parsed = Date.parse(agent.enrolledAt ?? "");
          return Number.isFinite(parsed) ? parsed : 0;
        }
        case "status": return enrolledAgentStatus(agent);
        case "label":
        default: return agent.label ?? "";
      }
    };
    const direction = sortDirection === "ascending" ? 1 : -1;
    return [...agents].sort((left, right) => {
      const compared = compareEnrolledValues(value(left), value(right));
      if (compared !== 0) return compared * direction;
      return left.agentId.localeCompare(right.agentId);
    });
  }, [agents, sortDirection, sortKey]);
  const setSort = (next: EnrolledAgentSortKey): void => {
    if (next === sortKey) {
      setSortDirection((current) => current === "ascending" ? "descending" : "ascending");
      return;
    }
    setSortKey(next);
    setSortDirection(next === "enrolledAt" ? "descending" : "ascending");
  };
  const ariaSort = (key: EnrolledAgentSortKey): "ascending" | "descending" | "none" =>
    key === sortKey ? sortDirection : "none";
  const heading = (key: EnrolledAgentSortKey, label: string): ReactNode => (
    <button
      type="button"
      className="svc-admin__sort-button"
      onClick={() => setSort(key)}
      aria-label={`Sort by ${label}; currently ${ariaSort(key)}`}
    >
      {label}
      {key === sortKey && (
        <span className="svc-admin__sort-indicator" aria-hidden="true">
          {sortDirection === "ascending" ? "↑" : "↓"}
        </span>
      )}
    </button>
  );
  if (query.isError) {
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load enrolled agents</strong>
        <span>The AIS admin service could not be reached. {query.error.message}</span>
      </div>
    );
  }
  if (query.isPending) {
    return <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching enrolled agents.</span></div>;
  }
  if (agents.length === 0) {
    return <div className="state-box" data-kind="empty"><strong>No agents enrolled yet.</strong></div>;
  }
  return (
    <table className="svc-adm2__table">
      <thead>
        <tr>
          <th aria-sort={ariaSort("label")}>{heading("label", "Label")}</th>
          <th aria-sort={ariaSort("agentId")}>{heading("agentId", "Agent ID")}</th>
          <th aria-sort={ariaSort("principal")}>{heading("principal", "Principal")}</th>
          <th aria-sort={ariaSort("platform")}>{heading("platform", "Platform")}</th>
          <th aria-sort={ariaSort("enrolledAt")}>{heading("enrolledAt", "Enrolled")}</th>
          <th aria-sort={ariaSort("status")}>{heading("status", "Status")}</th>
        </tr>
      </thead>
      <tbody>
        {sortedAgents.map((a) => {
          const status = enrolledAgentStatus(a);
          const statusLevel = status === "Revoked" ? "error" : status === "Suspended" ? "warn" : "healthy";
          return (
            <tr key={a.agentId}>
              <td>{a.label ?? "—"}</td>
              <td className="svc-adm2__mono">{a.agentId}</td>
              <td className="svc-adm2__mono">{a.principal?.email ?? "—"}</td>
              <td>{a.platform ?? "—"}</td>
              <td className="svc-adm2__mono">{a.enrolledAt !== undefined ? safeDate(a.enrolledAt) : "—"}</td>
              <td>
                <StatusBadge level={statusLevel} label={status} size="sm" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** A compact dashboard stat tile — the same visual unit the VCI console uses. */
function StatTile(props: { label: string; value: string; caption?: ReactNode; note?: string }): ReactNode {
  return (
    <div className="svc-admin__stat" title={props.note}>
      <span className="svc-admin__stat-value">{props.value}</span>
      <span className="svc-admin__stat-label">{props.label}</span>
      {props.caption !== undefined && <span className="svc-admin__stat-caption">{props.caption}</span>}
      {props.note !== undefined && <span className="svc-admin__stat-sr">{props.note}</span>}
    </div>
  );
}

function safeDate(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? formatDateTime(ms) : iso;
}
